import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 20;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", payment_status: null, attempts: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", payment_status: "missing_session" }); return; }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setState({ status: data.payment_status === "paid" ? "paid" : "polling", payment_status: data.payment_status, attempts });
        if (data.payment_status === "paid") { await refresh(); return; }
        if (["failed", "expired", "not_found"].includes(data.payment_status)) {
          setState({ status: "error", payment_status: data.payment_status, attempts });
          return;
        }
      } catch { /* keep polling */ }
      if (attempts >= MAX_POLLS) { setState({ status: "timeout", payment_status: "pending", attempts }); return; }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-[calc(100vh-80px)] bg-slate-50 flex items-center justify-center px-6 py-20">
      <div className="max-w-[520px] w-full bg-white border border-slate-200 p-10 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 data-testid="payment-loading" className="w-10 h-10 text-brand-orange animate-spin mx-auto mb-4" />
            <div className="eyebrow mb-2">Payment</div>
            <h1 className="font-serif text-2xl text-brand-blue mb-3 leading-snug">Confirming your subscription...</h1>
            <p className="text-slate-600 text-sm">Stripe is processing. This usually takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 data-testid="payment-paid" className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <div className="eyebrow text-brand-orange mb-2">Welcome to full access</div>
            <h1 className="font-serif text-2xl text-brand-blue mb-3 leading-snug">You are a paid member.</h1>
            <p className="text-slate-600 text-sm mb-8">Every editable template, tool and guide is now yours. A receipt has been emailed by Stripe.</p>
            <button data-testid="go-to-hub" onClick={() => navigate("/member/hub")} className="btn-primary w-full justify-center">
              Go to Member Hub <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {(state.status === "error" || state.status === "timeout") && (
          <>
            <XCircle data-testid="payment-error" className="w-10 h-10 text-slate-400 mx-auto mb-4" />
            <div className="eyebrow mb-2">Payment</div>
            <h1 className="font-serif text-2xl text-brand-blue mb-3 leading-snug">
              {state.status === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-slate-600 text-sm mb-8">
              {state.status === "timeout"
                ? "This is taking longer than expected. Your subscription will activate automatically once Stripe confirms. You can safely close this page."
                : "We could not confirm your payment. If you were charged, membership will activate on the next webhook. Otherwise please try again."}
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/member/hub" data-testid="back-to-hub" className="btn-primary w-full justify-center">Back to Member Hub</Link>
              <Link to="/member/account" className="text-xs uppercase tracking-widest text-slate-500 hover:text-brand-orange">Try again from Account</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="min-h-[calc(100vh-80px)] bg-slate-50 flex items-center justify-center px-6 py-20">
      <div className="max-w-[520px] w-full bg-white border border-slate-200 p-10 text-center">
        <XCircle className="w-10 h-10 text-slate-400 mx-auto mb-4" />
        <div className="eyebrow mb-2">Payment</div>
        <h1 className="font-serif text-2xl text-brand-blue mb-3 leading-snug">Checkout cancelled.</h1>
        <p className="text-slate-600 text-sm mb-8">No charge was made. You can restart the upgrade any time from your account.</p>
        <div className="flex flex-col gap-3">
          <Link to="/member/account" data-testid="retry-checkout" className="btn-primary w-full justify-center">Return to Account</Link>
          <Link to="/member/hub" className="text-xs uppercase tracking-widest text-slate-500 hover:text-brand-orange">Back to Member Hub</Link>
        </div>
      </div>
    </div>
  );
}
